import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal } from "../lib/motion";
import { useSEO, useLenis } from "../lib/hooks";
import { getPost, BLOG_POSTS } from "../data/blogPosts";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  useLenis();
  useSEO({
    title: post
      ? `${post.title} — JSC Blog`
      : "JSC Blog — Fashion Retail Insights",
    description: post
      ? post.teaser
      : "Fashion retail insights and data analytics for Indian retailers from Jay Shah Consultancy.",
    keywords:
      "fashion retail analytics India, retail data insights, Indian fashion retail trends, ForesightFlow, Jay Shah Consultancy, inventory intelligence software",
  });

  if (!post) return <Navigate to="/blogs" replace />;

  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

  return (
    <div className="jsc-grain relative bg-[#FAF8F5] min-h-screen" data-testid="page-blog-post">
      <Navbar />

      <article className="relative pt-36 md:pt-44 lg:pt-48 pb-20">
        <div className="jsc-gridlines absolute inset-0 top-0 h-[420px] opacity-25 [mask-image:linear-gradient(black,transparent)]" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-8">
          <Link
            to="/blogs"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 font-body text-sm text-[#3E4A5C] hover:text-[#1C1C1E] transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" /> All insights
          </Link>

          <Reveal>
            <p className="mt-8 font-body text-xs uppercase tracking-[0.22em] text-[#B39B77]">
              {post.feature}
            </p>
            <h1
              data-testid="blog-post-title"
              className="mt-4 font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight"
            >
              {post.title}
            </h1>
            <p className="mt-6 font-body text-sm text-[#3E4A5C]">
              Referenced from <span className="text-[#1C1C1E]">{post.source}</span>
            </p>
          </Reveal>

          {/* What the Article Says */}
          <Reveal delay={0.1}>
            <section className="mt-14">
              <h2 className="font-display text-2xl md:text-3xl text-[#1C1C1E]">What the Article Says</h2>
              <div className="mt-4 h-px w-16 bg-[#B39B77]" />
              <p className="mt-6 font-body text-lg leading-relaxed text-[#3E4A5C]">{post.whatItSays}</p>
            </section>
          </Reveal>

          {/* What JSC Experts See in It */}
          <Reveal delay={0.15}>
            <section className="mt-14 bg-[#F0EBE1]/50 border border-[rgba(28,28,30,0.08)] rounded-[3px] p-8 md:p-10">
              <h2 className="font-display text-2xl md:text-3xl text-[#1C1C1E]">What JSC Experts See in It</h2>
              <div className="mt-4 h-px w-16 bg-[#B39B77]" />
              <p className="mt-6 font-body text-lg leading-relaxed text-[#3E4A5C]">{post.whatJSCSees}</p>
              <Link
                to={post.featureLink}
                data-testid="blog-feature-link"
                className="mt-8 inline-flex items-center gap-2 font-body text-base font-medium text-[#1C1C1E] link-underline w-fit"
              >
                {post.featureAnchor}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </section>
          </Reveal>

          {/* Reference */}
          <Reveal delay={0.2}>
            <section className="mt-12 border-t border-[rgba(28,28,30,0.1)] pt-6">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#3E4A5C] mb-2">Reference</p>
              <a
                href={post.sourceUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                data-testid="blog-reference-link"
                className="inline-flex items-start gap-2 font-body text-sm text-[#3E4A5C] hover:text-[#1C1C1E] transition-colors duration-300 break-words"
              >
                <ExternalLink className="w-4 h-4 mt-0.5 shrink-0 text-[#B39B77]" />
                <span>{post.source} — {post.sourceUrl}</span>
              </a>
            </section>
          </Reveal>

          {/* Next post */}
          <Reveal delay={0.2}>
            <Link
              to={`/blogs/${next.slug}`}
              data-testid="blog-next-link"
              className="group mt-16 flex items-center justify-between gap-6 border-t border-[rgba(28,28,30,0.1)] pt-8"
            >
              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-[#B39B77] mb-2">Read next</p>
                <p className="font-display text-xl md:text-2xl text-[#1C1C1E] group-hover:text-[#3E4A5C] transition-colors duration-300">
                  {next.title}
                </p>
              </div>
              <ArrowUpRight className="w-6 h-6 shrink-0 text-[#1C1C1E] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </article>

      <CtaBand
        eyebrow="Make it personal"
        title="Want insights like this built around your own store's data?"
        testid="blog-post-cta"
      />

      <Footer />
    </div>
  );
};

export default BlogPost;
